import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ITopicDnDResponseOptionalGuid } from "Data/interfaces/Activities/IDragAndDropDataFormat";
import { getActiveComment } from "Store/Activities/Activities.selector";
import { Http } from "Utils/Http";
import Tabs, { ITabMap, TabsRef } from "../Tabs";
import Attachments from "../Attachments";
import History from "../History";
import Comments from "../Comments";
import useActionsContent from "./useActionsContent";
import ViewpointTab from "./ViewpointTab";
import { TypeMoreActionsType } from "Pages/Activities/useActivity";

interface IActionsContent {
  csId: number;
  updateActivity: ITopicDnDResponseOptionalGuid | null;
  loadingExternalLink: boolean;
  tempMoreActionsType?: TypeMoreActionsType;
  onOpenViewpoint?: (topicId: string, viewpointId: string) => void;
}

const ActionsContent: FC<IActionsContent> = ({
  csId,
  updateActivity,
  loadingExternalLink,
  tempMoreActionsType,
  onOpenViewpoint,
}) => {
  const { t } = useTranslation('activities');
  const activeComment = useSelector(getActiveComment);
  const workCommentParams = Http.GetQueryParams('workComment');
  const tabsRef = useRef<TabsRef>(null);
  const [workComment, setWorkComment] = useState<string | null>(workCommentParams);

  useActionsContent({
    tabsRef,
    updateActivity,
    activeComment,
    workComment,
    loadingExternalLink,
  });

  const newCommentsCount = useMemo(() => {
    return activeComment.comments.reduce((acc, comment) => {
      if (comment.DateTimeFormated.isAfter(activeComment.lastReadAt)) {
        acc++;
      }
      return acc;
    }, 0);
  }, [activeComment]);

  // The viewer only expects the very first viewpoint attached to the activity today.
  const primaryViewpoint = updateActivity?.Viewpoints?.[0];
  const canOpenViewpoint = Boolean(
    onOpenViewpoint &&
    updateActivity?.Guid &&
    primaryViewpoint?.Guid
  );

  const items = useMemo(() => {
    const opts: ITabMap = new Map();

    if (canOpenViewpoint && updateActivity?.Guid && primaryViewpoint?.Guid) {
      opts.set('viewpoint', {
        id: 'viewpoint',
        label: t('actionsContent.tabViewpoint'),
        isDisabled: false,
        children: (
          <ViewpointTab
            topicId={updateActivity.Guid}
            viewpointId={primaryViewpoint.Guid}
            snapshotUrl={primaryViewpoint.SnapshotFileUrl || null}
            loadingExternalLink={loadingExternalLink}
            onViewInViewer={() => {
              // Keep the handler inline to document the decision: we only open when the hook says so.
              if (canOpenViewpoint && updateActivity.Guid && primaryViewpoint?.Guid) {
                onOpenViewpoint?.(updateActivity.Guid, primaryViewpoint.Guid);
              }
            }}
          />
        ),
      });
    }

    opts.set('attachement', {
      id: 'attachement',
      label: t('actionsContent.tabAttachments'),
      isDisabled: false,
      tagNumber: updateActivity?.Attachments.length,
      children: (
        <Attachments
          csId={csId}
          updateActivity={updateActivity}
          loadingExternalLink={loadingExternalLink}
        />
      ),
    });

    if (!!updateActivity && !!updateActivity?.Guid) {
      opts.set('comments', {
        id: 'comments',
        label: t('actionsContent.tabComments'),
        isDisabled: false,
        tagNumber: activeComment.comments.length,
        newTagNumber: newCommentsCount,
        customContentPadding: '0',
        children: (
          <Comments
            updateActivity={updateActivity}
            loadingExternalLink={loadingExternalLink}
            workComment={workComment}
            onClearWorkComment={() => setWorkComment(null)}
          />
        ),
      });
      opts.set('history', {
        id: 'history',
        label: t('actionsContent.tabHistory'),
        isDisabled: false,
        children: (
          <History
            csId={csId}
            updateActivity={updateActivity}
            loadingExternalLink={loadingExternalLink}
          />
        ),
        customContentPadding: '7px 14px',
      });
    }

    return opts;
  }, [
    csId,
    loadingExternalLink,
    updateActivity,
    activeComment?.comments.length,
    workComment,
    newCommentsCount,
    canOpenViewpoint,
    primaryViewpoint?.Guid,
    primaryViewpoint?.SnapshotFileUrl,
    onOpenViewpoint,
  ]);

  const defaultTab = canOpenViewpoint ? 'viewpoint' : 'attachement';

  useEffect(() => {
    if (tempMoreActionsType === 'duplicate') {
      tabsRef.current?.onActiveTab('attachement');
    }
  }, [tempMoreActionsType]);

  return (
    <Tabs
      ref={tabsRef}
      defaultActiveTab={defaultTab}
      options={items}
      loadingExternalLink={loadingExternalLink}
    />
  );
}

export default ActionsContent;
